﻿import React, { useState, useEffect } from 'react';
import { HplcInstrumentData } from '../models/hplc-instrument-data';
import { HplcAsset } from '../models/hplc-asset';
import { ItemBasicInformation } from '../models/item-basic-information';
import ItemBasicInformationDialog from './ItemBasicInformationDialog';
import { NumberBoxComponent } from './NumberBoxComponent';
import { CrudFunctions } from '../utilities/crud-functions';
import { FieldUpdate } from '../models/field-update';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { BenchSheetTechnologyType, ModalMessageType } from '../models/enums';
import SecondaryBenchSheetInstrument from './SecondaryBenchSheetInstrument';

import Button from 'devextreme-react/button'
import { Popup as DialogPopup } from 'devextreme-react/popup';

let key = 1;

export interface Props {
    benchSheetId: string;
    isReadOnly?: boolean;
    onInstrumentChanged?: (instrumentId?: string) => void;
}

export default function HplcInstrument(props: Props) {
    const [instruments, setInstruments] =
        useState<HplcAsset[]>();

    const [selectedInstrument, setSelectedInstrument] =
        useState<HplcAsset>();

    const [instrumentData, setInstrumentData] =
        useState<HplcInstrumentData>();

    const [selectedInstrumentData, setSelectedInstrumentData] =
        useState<string>();

    const [itemBasicInformation, setItemBasicInformation] =
        useState<ItemBasicInformation>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    const [dataLoaded, setDataLoaded] =
        useState<boolean>();

    useEffect(() => {
        getHplcInstrumentData();
    }, [props.benchSheetId]);

    useEffect(() => {
        getAllData();
    }, [instrumentData]);

    key++;

    return (
        <div>
            <div className="lq-header pw-margin-top-05 aurum-blue-bg">
                <div className="lq-header-title">HPLC Instrument</div>
                <div className="title-bar-spacer" />
                <div>
                    <Button className="title-bar-button"
                        text="" icon="info" type="success"
                        hint="item information"
                        onClick={showInformationDialog} />
                </div>
            </div>

            <SecondaryBenchSheetInstrument
                benchSheetId={props.benchSheetId}
                isReadOnly={props.isReadOnly}
                technology={BenchSheetTechnologyType.HighPerformanceLiquidChromatography}
                showHeader={false}
            />

            <div className="pw-form-section">
                <div className="pw-flex-wrapped-row-section pw-margin-top-05">
                    <div id="instrument-right-pane" className="pw-margin-left-1">
                        <div className="tab-subtitle pw-blue">Sampler</div>

                        <div className="pw-comp-row">
                            <div className="pw-form-unit pw-comp-15 pw-comp-row-align-bottom">
                                <label className="pw-form-unit-label">Injection Volume</label>
                                <NumberBoxComponent id="injectionVolume" readOnly={props.isReadOnly}
                                    fixedUnit={'μL'} onValueChanged={onValueChanged}
                                    value={instrumentData ? instrumentData.injectionVolume : undefined} />
                            </div>
                            <div className="pw-form-unit pw-comp-15 pw-comp-row-align-bottom">
                                <label className="pw-form-unit-label">Cooled AutoSampler</label>
                                <NumberBoxComponent id="cooledAutoSamplerTemperature"
                                    format="#" readOnly={props.isReadOnly}
                                    fixedUnit={'°C'} onValueChanged={onValueChanged}
                                    value={instrumentData ? instrumentData.cooledAutoSamplerTemperature : undefined} />
                            </div>
                        </div>

                        <div className="tab-subtitle pw-blue">Pump</div>

                        <div className="pw-comp-row">
                            <div className="pw-form-unit pw-comp-15 pw-margin-top-05">
                                <div className="pw-form-unit-label">Type</div>
                                <div className="pw-readonly-label">{selectedInstrument &&
                                    selectedInstrument.pumpType &&
                                    selectedInstrument.pumpType.name ?
                                    selectedInstrument.pumpType.name : 'NA'}</div>
                                <div className="pw-form-unit-label">Max Pressure</div>
                                <div className="pw-readonly-label">{selectedInstrument &&
                                    selectedInstrument.pumpType &&
                                    selectedInstrument.pumpMaxPressure ?
                                    selectedInstrument.pumpMaxPressure : 'NA'}</div>
                            </div>

                            <div className="pw-form-unit pw-comp-15 pw-comp-row-align-bottom">
                                <label className="pw-form-unit-label">System Pressure</label>
                                <div className="pw-range-container">
                                    <NumberBoxComponent id="pumpSystemPressureStart"
                                        format="#" readOnly={props.isReadOnly}
                                        containerClass="number-box-component-no-units"
                                        onValueChanged={onValueChanged}
                                        value={instrumentData ? instrumentData.pumpSystemPressureStart : undefined} />
                                    <div>-</div>

                                    <NumberBoxComponent id="pumpSystemPressureEnd"
                                        format="#" readOnly={props.isReadOnly}
                                        containerClass="pw-range-container-units-side"
                                        fixedUnit={'bar'} onValueChanged={onValueChanged}
                                        value={instrumentData ? instrumentData.pumpSystemPressureEnd : undefined} />
                                </div>
                                <label className="pw-form-unit-label">System Flow Rate</label>
                                <NumberBoxComponent id="pumpSystemFlowRate" readOnly={props.isReadOnly}
                                    fixedUnit={'mL/min'} onValueChanged={onValueChanged}
                                    value={instrumentData ? instrumentData.pumpSystemFlowRate : undefined} />
                            </div>
                        </div>
                    </div>
                </div>

                {itemBasicInformationDialogVisible &&
                    <DialogPopup visible={itemBasicInformationDialogVisible}
                        onHiding={hideItemBasicInformationDialog}
                        title="Item Basic Information"
                        width={'25rem'}
                        height={'21rem'}
                        dragEnabled={false}
                        resizeEnabled={false}>
                        <ItemBasicInformationDialog initInformation={itemBasicInformation}
                            onOkClick={hideItemBasicInformationDialog}
                            key={`IBI${key}`} />
                    </DialogPopup>
                }
            </div>

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }

        </div>
    );

    function getMatchingInstrument() {
        if (instruments && instrumentData && instrumentData.instrumentId) {
            const matchingInstrument = instruments.find(i => i.assetId === instrumentData.instrumentId);

            if (matchingInstrument)
                setSelectedInstrument(matchingInstrument);
        }
    }

    async function onValueChanged(e: any) {
        //console.log("onValueChanged");
        //console.log(e);

        if (!dataLoaded)
            return;

        if (e.event && e.event.data && e.event.data.prop) {
            const caller = e.event.data.prop;

            setInstrumentData({ ...instrumentData, [caller]: e.value })

            let fieldUpdate: FieldUpdate = {
                entity: "HplcBenchSheets",
                field: caller,
                value: e.value,
                rowId: props.benchSheetId
            };

            //todo: also set UOMs (which are fixed but we want in the database anyway)
            await updateHplcBenchSheetField(fieldUpdate)
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Updating Instrument Field");
                });
        }
    }

    async function onInstrumentChanged(e: any) {
        console.log("onInstrumentChanged");
        console.log(e);
        if (!dataLoaded || !instruments)
            return;

        if (instrumentData && instrumentData.instrumentId &&
            instrumentData.instrumentId === e.value)
            return;

        if (instruments) {
            setInstrumentData({ ...instrumentData, instrumentId: e.value })

            let fieldUpdate: FieldUpdate = {
                entity: "BenchSheets",
                field: "instrumentId",
                value: e.value,
                rowId: props.benchSheetId
            };

            await updateBenchSheetField(fieldUpdate)
                .then(() => {
                    if (props.onInstrumentChanged)
                        props.onInstrumentChanged(e.value);

                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Updating Bench Sheet Field");
                });
        }

        /* TODO:
        if (selectedInstrument.length > 0) {
            if (benchSheet && benchSheet.detectors && benchSheet.detectors.length > 0) {
                const modalContext: XXXContext = {
                    modalType: ConfirmationModal,
                    title: "Instrument Change",
                    message: "Are you sure you want to change the instrument? Detector entries may be lost with this change.",
                    onModalConfirmed: onInstrumentChangeConfirmed,
                    onModalCancelled: onInstrumentChangedCancelled
                };

                XXX.publish(ModalSubscriberTopic, modalContext);
                return;
            }

            onInstrumentChangeConfirmed();
        }
        */
    }

    function onInstrumentChangeConfirmed() {
        let selectedInstrument = instruments.find(i => i.assetId === selectedInstrumentData);

        if (selectedInstrument) {
            setSelectedInstrument(selectedInstrument);

            // TODO - send to parent:
            {/*
            getDetectorList()
                .then(() => {
                    updateField("BenchSheets", "instrumentId", selectedInstrumentData)
                        .catch((response: ResponseError) => {
                            XXX.publish(ModalSubscriberTopic, { modalType: ApiResponseErrorModal, responseError: response });
                        });

                    setSelectedInstrumentData(undefined);
                });
                */}
        }
    }

    function onInstrumentChangedCancelled() {
        setSelectedInstrumentData(undefined);
    }

    async function getHplcInstrumentData() {
        await CrudFunctions.getData(`benchsheets/hplcinstrumentdata/${props.benchSheetId}`)
            .then(data => {
                setInstrumentData(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Instrument Data");
            });
    }

    async function getInstruments() {
        await CrudFunctions.getData("benchsheets/hplcinstruments")
            .then(data => {
                setInstruments(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Instruments");
            });
    }

    async function updateHplcBenchSheetField(fieldUpdate: FieldUpdate) {
        return await CrudFunctions.putData("benchsheets/hplcbenchsheetfield", fieldUpdate);
    }

    async function updateBenchSheetField(fieldUpdate: FieldUpdate) {
        return await CrudFunctions.putData("benchsheets/benchsheetfield", fieldUpdate);
    }

    async function getAllData() {
        await Promise.all([
            getInstruments()
        ])
            .then(() => {
                getMatchingInstrument();
                setDataLoaded(true);
            })
    }

    function showInformationDialog() {
        if (selectedInstrument) {
            let itemBasicInformation: ItemBasicInformation = {
                rowPointer: selectedInstrument.assetId,
                createTimestamp: selectedInstrument.createTimestamp,
                lastModifiedTimestamp: selectedInstrument.modifyTimestamp,
                createUser: selectedInstrument.createUser,
                modifyUser: selectedInstrument.modifyUser
            };

            setItemBasicInformation(itemBasicInformation);
            setItemBasicInformationDialogVisible(true);
        }
    }

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }
}

